const _ = require("lodash");
const {findArguments} = require("../util/ParserHelpers");
const {getDigitWidth, getLineWidth} = require("../util/RenderHelpers");
const ColorUtil = require("../../util/ColorUtil");
const Font = require("../../util/Font");
const Math2D = require("../../util/Math2D");

const makeParser = function(settings = {}) {
	return function(ps) {
		const nodeType = findArguments(ps, 1)[0].getRest();
		const possibleTypes = ["hex", "square"];
		if (!possibleTypes.includes(nodeType)) {
			ps.pushError(
				`Optional argument must be the desired type. Possible values are ${possibleTypes
					.map(name => `"${name}"`)
					.join(", ")}.`
			);
			return false;
		}
		const reqArgs = findArguments(ps, 1);
		const children = reqArgs
			.map(reqArg => reqArg.parseMath(reqArg)[0] || null)
			.filter(x => x !== null);
		return [
			{
				type: `${nodeType}SkipCountCell`,
				children,
			},
		];
	};
};

const hexCellNode = {
	draw(rc, x, y, node, measure) {
		const {cellW, r, fontInfo, contentMeasure} = measure;
		const cell = node.children[0];
		const cx = x + cellW / 2;
		const cy = y;
		// Draw the hexagon
		rc.context.save();
		rc.context.lineWidth = getLineWidth(rc, fontInfo);
		rc.context.lineCap = "round";
		rc.context
			.bp()
			.moveTo(cx, cy - r)
			.lineTo(cx + cellW / 2, cy - r / 2)
			.lineTo(cx + cellW / 2, cy + r / 2)
			.lineTo(cx, cy + r)
			.lineTo(cx - cellW / 2, cy + r / 2)
			.lineTo(cx - cellW / 2, cy - r / 2)
			.lineTo(cx, cy - r)
			.stroke();
		rc.context.restore();

		// Draw the cell contents
		if (node.children.length) {
			const contentX = cx - contentMeasure[0].width / 2;
			const contentY = cy;
			rc.drawNode(rc, contentX, contentY, cell, contentMeasure[0]);
		}
	},
	measure(rc, fontInfo, props, node) {
		const lineWidth = getLineWidth(rc, fontInfo);
		const fontSize = fontInfo.size;
		const diam = fontSize * 3;
		const r = diam / 2;
		const cellW = (diam * Math.sqrt(3)) / 2;
		const contentMeasure = node.children.map(cell =>
			rc.measureNode(rc, fontInfo, props, cell)
		);
		return {
			width: cellW + lineWidth / 2,
			top: r,
			bottom: r,
			fontInfo,
			cellW,
			r,
			contentMeasure,
		};
	},
	readAloud: {children: true},
};

// NOTE: this square cell was heavily inspired (stolen and repurposed from) by "\hundredsChartSection"
// This command is meant to mimic the view of a single cell of that grid.

const squareCellNode = {
	draw(rc, x, y, node, measure) {
		const {context} = rc;
		context.save();

		// setup default values
		const {fontInfo, top, squareSize, margin, contentMeasure} = measure;
		const topY = y - top;
		const lineWidth = getLineWidth(rc, fontInfo);
		const {
			lighterGrayFill: strokeStyle,
			defaultBlackFill: fillStyle, // also used for text
		} = ColorUtil.getCurrentScheme();

		// context defaults
		context.font = Font.fromInfo(fontInfo);
		context.lineWidth = lineWidth;
		context.strokeStyle = strokeStyle;
		context.fillStyle = fillStyle;
		context.globalAlpha = 1;

		// draw the table!
		context.beginPath();
		const startX = x;
		const startY = topY;
		const rect = [startX, startY, squareSize, squareSize];
		const paddedRect = Math2D.padRect(rect, -margin);

		const isBlank = node.children.length
			? node.children[0].type === "blank"
			: false;

		const [padX, padY, padWidth, padHeight] = paddedRect;
		if (isBlank) {
			// Draw blanks as perfect squares
			rc.pushBlank({
				type: "simple",
				x: padX,
				y: padY,
				width: padWidth,
				height: padHeight,
				answerSize: node.children[0].answerSize,
			});
		} else {
			// Draw the contents
			if (node.children.length) {
				const contentX = startX + squareSize / 2 - contentMeasure[0].width / 2;
				const contentY = y;
				rc.drawNode(
					rc,
					contentX,
					contentY,
					node.children[0],
					contentMeasure[0]
				);
			}

			// Draw the square
			context.strokeRect(...paddedRect);
		}

		context.restore();
	},
	measure(rc, fontInfo, props, node) {
		// const gridDimensions = getGridDimensions(node.options);

		const lineWidth = getLineWidth(rc, fontInfo);
		const maxNumSize = 3 * getDigitWidth(rc, fontInfo);
		const squareSize = maxNumSize + lineWidth * 4;
		const margin = squareSize * 0.04;

		// measure children for the cache entry
		const contentMeasure = node.children.map(cell =>
			rc.measureNode(rc, fontInfo, props, cell)
		);

		return _.extend({
			squareSize,
			width: squareSize,
			top: (1 / 2) * squareSize,
			bottom: (1 / 2) * squareSize,
			fontInfo,
			margin,
			contentMeasure,
		});
	},
	// readAloud: probably need to read the color somehow
	readAloud: {children: true},
};

module.exports = {
	parsers: {
		mathCommands: {
			skipcountcell: makeParser(),
		},
	},
	nodes: {
		squareSkipCountCell: squareCellNode,
		hexSkipCountCell: hexCellNode,
	},
};
